import { isPlatformBrowser } from '@angular/common';
import { Directive, HostBinding, HostListener, Inject, Input, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';

/**
 * The current live chat implementation injects a div into any element with a matched id.
 * The injected div holds an element [transparent gif] with a click event bound to it which when clicked opens the chat window.
 * Here we're passing the click event of any given element to which this directive is bound to that gif, whilst
 * keeping the chat html out of view so as not to break any component compositions
 */
@Directive({
  selector: '[vmLiveChatV3]',
})
export class LiveChatV3Directive implements OnDestroy, OnInit {
  @Input() vmLiveChatV3 = '';

  protected $chatContainerEl: HTMLDivElement;
  protected $chatClickEl: HTMLElement;
  protected observer: MutationObserver;

  @HostBinding('attr.data-chat-enabled')
  protected chatEnabled = false;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  @HostListener('click')
  onClick() {
    if (!this.chatEnabled) {
      return;
    }
    this.$chatClickEl?.click();
  }

  ngOnInit() {
    this.$chatContainerEl = this.getChatContainer();
    this.attachedClickEvent();
    if (isPlatformBrowser(this.platformId)) {
      this.initChatObserver();
    }
  }

  protected getChatContainer(): HTMLDivElement {
    const $existingContainer = document.getElementById(this.vmLiveChatV3);

    if ($existingContainer !== null) {
      return $existingContainer as HTMLDivElement;
    }

    const $container = document.createElement('div');
    $container.style.display = 'none';
    $container.id = this.vmLiveChatV3;

    document.body.appendChild($container);

    return document.getElementById(this.vmLiveChatV3) as HTMLDivElement;
  }

  protected initChatObserver() {
    this.observer = new MutationObserver((mutations) => {
      const codeInjected = Boolean(mutations.find((mutation) => mutation.type === 'childList'));
      if (!codeInjected) {
        this.chatEnabled = false;
        return;
      }

      setTimeout(() => {
        //Timeout here to allow time for dom element to become available
        this.attachedClickEvent();
      }, 300);
    });

    this.observer.observe(this.$chatContainerEl, { childList: true });
  }

  protected attachedClickEvent() {
    const $clickableElement = this.$chatContainerEl.querySelector('[data-lp-event=click]');
    this.chatEnabled = $clickableElement !== null;
    this.$chatClickEl = $clickableElement as HTMLElement;
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      this.observer.disconnect();
    }
  }
}
